import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { toast } from "sonner";
import { mergeGuestAccountRequest } from "@ano/shared/api/guest";
import type { InviteAcceptSuccess } from "@ano/shared/api/invites";
import { API_URL } from "@/lib/config";

interface UserLike {
  id: string;
}

interface WorkspaceLike {
  id: string;
  slug?: string | null;
}

/**
 * Initialize the invite token from URL / localStorage. Stays a separate
 * hook so the token is available to other hooks (e.g. `useWorkspaceScope`
 * which gates subdomain pinning on it) before the side-effect handlers
 * are constructed.
 */
export function useInviteToken() {
  // Parse invite token from URL (?invite= or legacy ?space_invite=) with
  // localStorage fallback — the token is persisted before OAuth so it survives
  // cross-subdomain redirects where sessionStorage is per-origin.
  // Consume-on-read: remove from localStorage immediately to prevent stale
  // tokens from triggering the overlay on future app opens.
  return useState<string | null>(() => {
    // After guest signup, the token is already consumed — don't re-resolve
    if (localStorage.getItem("ano-guest-merge-pending")) return null;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("invite") ?? params.get("space_invite");
    if (fromUrl) return fromUrl;
    try {
      const raw = localStorage.getItem("ano-pending-invite-token");
      if (!raw) return null;
      localStorage.removeItem("ano-pending-invite-token");
      const parsed = JSON.parse(raw) as { token?: string; ts?: number };
      if (parsed?.token && parsed?.ts && Date.now() - parsed.ts < 3_600_000) {
        return parsed.token;
      }
      return null;
    } catch {
      return null;
    }
  });
}

interface UseInviteFlowArgs {
  user: UserLike | null | undefined;
  workspaceList: ReadonlyArray<WorkspaceLike>;
  inviteToken: string | null;
  setInviteToken: (token: string | null) => void;
  setScope: (scope: string) => void;
  setSelectedChannel: (id: string | null) => void;
  setPendingWorkspace: (id: string | null) => void;
  navigateToSubdomain: (slug: string) => boolean;
}

/**
 * Owns the side-effects + handler callbacks tied to an invite token:
 *   - Force home mode while the token is in play.
 *   - Run the guest-merge handoff once an authenticated session arrives
 *     behind a guest invite.
 *   - Build `handleInviteNavigation` / `handleInviteTokenConsumed`
 *     handlers consumed by `<InboxView>`.
 */
export function useInviteFlow({
  user,
  workspaceList,
  inviteToken,
  setInviteToken,
  setScope,
  setSelectedChannel,
  setPendingWorkspace,
  navigateToSubdomain,
}: UseInviteFlowArgs) {
  // Force home mode when arriving with an invite token
  useEffect(() => {
    if (inviteToken) {
      setScope("home");
      setSelectedChannel(null);
    }
  }, [inviteToken, setScope, setSelectedChannel]);

  // Guest merge handoff: if the user just signed up from guest mode,
  // merge the guest account data into their new authenticated account.
  const { getAccessToken: getMergeAccessToken } = useAuth();
  useEffect(() => {
    const pending = localStorage.getItem("ano-guest-merge-pending");
    if (!pending || !user) return;
    localStorage.removeItem("ano-guest-merge-pending");

    let parsed: { session_id: string; browser_id: string; channel_id: string };
    try {
      parsed = JSON.parse(pending);
    } catch {
      return;
    }

    void (async () => {
      try {
        const accessToken = await getMergeAccessToken();
        if (!accessToken) return;

        await mergeGuestAccountRequest({
          apiBaseUrl: API_URL,
          sessionId: parsed.session_id,
          browserId: parsed.browser_id,
          accessToken,
        });

        // Clear invite token from URL after consumption.
        const params = new URLSearchParams(window.location.search);
        params.delete("invite");
        params.delete("space_invite");
        const cleaned = params.toString();
        window.history.replaceState(
          {},
          "",
          window.location.pathname + (cleaned ? `?${cleaned}` : ""),
        );
        localStorage.removeItem("ano-pending-invite-token");
        toast.success("Welcome! Your messages have been preserved.");
      } catch (err) {
        console.error("Guest merge failed:", err);
        // Non-fatal — guest data stays as-is, user continues normally
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  /** Shared navigation logic after accepting any invite type. */
  const handleInviteNavigation = useCallback(
    (result: InviteAcceptSuccess) => {
      if (result?.workspace_id && result?.channel_id) {
        const ws = workspaceList.find((w) => w.id === result.workspace_id);
        if (ws?.slug && navigateToSubdomain(ws.slug)) return;
        setPendingWorkspace(result.workspace_id);
        setScope(result.workspace_id);
        setSelectedChannel(result.channel_id);
      } else if (result?.workspace_id) {
        const ws = workspaceList.find((w) => w.id === result.workspace_id);
        if (ws?.slug && navigateToSubdomain(ws.slug)) return;
        setPendingWorkspace(result.workspace_id);
        setScope(result.workspace_id);
        setSelectedChannel(null);
      } else if (result?.channel_id) {
        setScope("home");
        setSelectedChannel(result.channel_id);
      }
    },
    [
      workspaceList,
      navigateToSubdomain,
      setPendingWorkspace,
      setScope,
      setSelectedChannel,
    ],
  );

  /** Clear invite URL param, localStorage backup, and in-memory token. */
  const handleInviteTokenConsumed = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete("invite");
    url.searchParams.delete("space_invite");
    window.history.replaceState({}, "", url.toString());
    try {
      localStorage.removeItem("ano-pending-invite-token");
    } catch {
      // ignore
    }
    setInviteToken(null);
  }, [setInviteToken]);

  return {
    handleInviteNavigation,
    handleInviteTokenConsumed,
  };
}
